import { postProcessRenewal } from 'modules/cog/cog-slice';
import { sessionId } from 'modules/global-wrapper';
import {
  isEcomClient,
  sessionClientURL,
  sessionCurrencyCode,
  sessionInvoiceNumber,
  sessionProducts,
  sessionQuoteId,
  sessionTotalAmount,
  sesssionCancelRedirectUrl,
} from 'modules/session-details';
import { GATrack } from 'modules/tracking';
import { GAEventNames } from 'modules/tracking/tracking-constants';
import { getPurchaseFlow, trackRevenue } from 'modules/tracking/tracking-module';
import { postProcessInvoice, postProcessQuote } from 'modules/ucs/ucs-slice';
import { PAYMENT_METHODS_TYPES, SEARCH_PARAMS } from 'utils/constants';
import { getProductAdminDataFromCookie, removeProductAdminDataFromCookie } from 'utils/ui-utils';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';

type PaymentType = (typeof PAYMENT_METHODS_TYPES)[keyof typeof PAYMENT_METHODS_TYPES];

const useProvisioning = () => {
  const dispatch = useAppDispatch();

  const selectedSessionId = useAppSelector(sessionId);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';

  // Session details selectors
  const selectedQuoteTotalPrice = useAppSelector(sessionTotalAmount);
  const selectedQuoteKey = useAppSelector(sessionQuoteId);
  const selectedSessionProducts = useAppSelector(sessionProducts);
  const selectedSessionCurrencyCode = useAppSelector(sessionCurrencyCode);
  const selectedSessionCancelUrl = useAppSelector(sesssionCancelRedirectUrl);
  const selectedClientIsEcom = useAppSelector(isEcomClient);
  const selectedSessionClientUrl = useAppSelector(sessionClientURL);

  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const productAdminData = getProductAdminDataFromCookie();

  const processQuote = (paymentType: PaymentType) => {
    const data = {
      quoteCode: selectedQuoteKey,
      payload: {
        grossAmount: selectedQuoteTotalPrice,
        sessionId: selectedSessionId,
        paymentType,
        ...(Object.keys(productAdminData).length > 0 && { productAdmin: productAdminData }),
      },
    };
    dispatch(postProcessQuote(data))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: paymentType,
          quoteId: selectedQuoteKey,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      )
      .catch(() =>
        GATrack(GAEventNames.PurchaseError, {
          products: selectedSessionProducts,
          paymentMethod: paymentType,
          errMsg: GAEventNames.PROVISIONING_FAILED,
        }),
      )
      .finally(() => Object.keys(productAdminData).length > 0 && removeProductAdminDataFromCookie());
  };

  const processRenewal = (paymentType: PaymentType) => {
    const payload = {
      date: renewalDate,
      sessionId: selectedSessionId,
      amount: selectedQuoteTotalPrice.toString(),
    };
    dispatch(postProcessRenewal(payload))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: paymentType,
          quoteId: selectedQuoteKey,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      );
  };

  const processInvoice = (paymentType: PaymentType) => {
    dispatch(postProcessInvoice({ sessionId: selectedSessionId }))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: paymentType,
          quoteId: selectedInvoiceNumber,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      );
  };

  return { processQuote, processRenewal, processInvoice };
};
export default useProvisioning;
